/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarMedium,
  ExternalLink,
  InlineTextButton,
  LinkedLogo,
  NamedLink,
  NotificationBadge,
} from '../../../../components';
import LanguageMenu from '../LanguageMenu/LanguageMenu';

import css from './TopbarMobileMenu.module.css';

const isSearchPage = page => page === 'SearchPage' || page === 'SearchPageWithListingType';

const getCurrentPageClass = (pageName, currentPage, route) => {
  const hasPageName = name => currentPage?.indexOf(name) === 0;
  const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `CMSPage:${pageId}`;
  const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `InboxPage:${tab}`;
  const isCurrentSearchPage = pageName === 'SearchPage' && isSearchPage(currentPage);

  return isCMSPage(route?.params?.pageId) ||
    isInboxPage(route?.params?.tab) ||
    isCurrentSearchPage ||
    currentPage === pageName
    ? css.navigationItemCurrent
    : null;
};

const MobileNavItem = props => {
  const { currentPage, pageName, route, children, onClick } = props;
  const itemClassName = classNames(
    css.navigationItem,
    getCurrentPageClass(pageName, currentPage, route)
  );

  if (onClick) {
    return (
      <li className={itemClassName}>
        <button type="button" className={css.navigationButton} onClick={onClick}>
          {children}
        </button>
      </li>
    );
  }

  const { name, params, to, href, type } = props;

  if (type === 'external') {
    return (
      <li className={itemClassName}>
        <ExternalLink href={href} className={css.navigationLink}>
          {children}
        </ExternalLink>
      </li>
    );
  }

  return (
    <li className={itemClassName}>
      <NamedLink name={name} params={params} to={to} className={css.navigationLink}>
        {children}
      </NamedLink>
    </li>
  );
};

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { text, type, href, route } = linkConfig;

  if (type === 'internal' && route) {
    return (
      <MobileNavItem
        name={route.name}
        params={route.params}
        to={route.to}
        pageName={route.name}
        route={route}
        currentPage={currentPage}
      >
        {text}
      </MobileNavItem>
    );
  }

  return (
    <MobileNavItem type="external" href={href} currentPage={currentPage}>
      {text}
    </MobileNavItem>
  );
};

const MobileMenuHeader = ({ onClose, config, intl }) => (
  <div className={css.header}>
    <button type="button" className={css.closeButton} onClick={onClose}>
      <FormattedMessage id="TopbarMobileMenu.close" defaultMessage="Close" />
    </button>
    <LinkedLogo
      id="logo-mobile-menu"
      className={css.logoLink}
      logoImageClassName={css.logoImage}
      logoImageMobile="/static/img/logo.svg"
      layout="mobile"
      alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
      linkToExternalSite={config?.topbar?.logoLink}
    />
  </div>
);

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @param {Function} props.onClose
 * @param {Function} props.onSearchClick
 * @param {Object} props.config
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    onClose,
    onSearchClick,
    config,
    showCreateListingsLink,
    showManageListingsLink,
  } = props;

  const intl = useIntl();
  const user = ensureCurrentUser(currentUser);

  const userType = user?.attributes?.profile?.publicData?.userType;
  const isServiceProvider = userType === 'service_provider' || userType === 'serviceprovider';
  const isAdmin = userType === 'admin';

  const extraLinks = customLinks.map((linkConfig, index) => (
    <CustomLinkComponent
      key={`${linkConfig.text}_${index}`}
      linkConfig={linkConfig}
      currentPage={currentPage}
    />
  ));

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage
      ? css.navigationItemCurrent
      : null;
  };

  if (!isAuthenticated) {
    return (
      <nav className={css.root} aria-label={intl.formatMessage({ id: 'Topbar.menuIcon' })}>
        <MobileMenuHeader onClose={onClose} config={config} intl={intl} />

        <ul className={css.navigationList}>
          <MobileNavItem name="LandingPage" pageName="LandingPage" currentPage={currentPage}>
            <FormattedMessage id="Topbar.nav.home" />
          </MobileNavItem>
          <MobileNavItem name="SearchPage" pageName="SearchPage" currentPage={currentPage}>
            <FormattedMessage id="Topbar.nav.discover" />
          </MobileNavItem>
          <MobileNavItem
            name="CMSPage"
            params={{ pageId: 'team' }}
            pageName="CMSPage"
            route={{ name: 'CMSPage', params: { pageId: 'team' } }}
            currentPage={currentPage}
          >
            <FormattedMessage id="Topbar.nav.team" />
          </MobileNavItem>
          <MobileNavItem
            name="CMSPage"
            params={{ pageId: 'media' }}
            pageName="CMSPage"
            route={{ name: 'CMSPage', params: { pageId: 'media' } }}
            currentPage={currentPage}
          >
            <FormattedMessage id="Topbar.nav.media" />
          </MobileNavItem>
          <MobileNavItem currentPage={currentPage} onClick={onSearchClick}>
            <FormattedMessage id="Topbar.nav.search" />
          </MobileNavItem>
          <MobileNavItem
            name="PrivateAccessPage"
            pageName="PrivateAccessPage"
            currentPage={currentPage}
          >
            <FormattedMessage id="Topbar.nav.privateAccess" />
          </MobileNavItem>
          <li className={css.languageNavigationItem}>
            <LanguageMenu mobile />
          </li>
          {extraLinks}
          <li className={css.navigationItem}>
            <NamedLink name="SignupPage" className={css.navigationLink}>
              <FormattedMessage id="TopbarMobileMenu.signupLink" />
            </NamedLink>
          </li>
          <li className={css.navigationItem}>
            <NamedLink name="LoginPage" className={css.navigationLink}>
              <FormattedMessage id="TopbarMobileMenu.loginLink" />
            </NamedLink>
          </li>
        </ul>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const manageListingsLinkMaybe =
    showManageListingsLink && !isServiceProvider ? (
      <li className={classNames(css.navigationItem, currentPageClass('ManageListingsPage'))}>
        <NamedLink name="ManageListingsPage" className={css.navigationLink}>
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink>
      </li>
    ) : null;

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <li className={classNames(css.navigationItem, currentPageClass('NewListingPage'))}>
      <NamedLink name="NewListingPage" className={css.navigationLink}>
        {isServiceProvider ? (
          <FormattedMessage id="TopbarMobileMenu.syncCalendar" />
        ) : isAdmin ? (
          <FormattedMessage id="TopbarMobileMenu.addExperience" />
        ) : (
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        )}
      </NamedLink>
    </li>
  ) : null;

  return (
    <nav className={css.root} aria-label={intl.formatMessage({ id: 'Topbar.menuIcon' })}>
      <MobileMenuHeader onClose={onClose} config={config} intl={intl} />

      <ul className={css.navigationList}>
        <MobileNavItem name="LandingPage" pageName="LandingPage" currentPage={currentPage}>
          <FormattedMessage id="Topbar.nav.home" />
        </MobileNavItem>
        <MobileNavItem name="SearchPage" pageName="SearchPage" currentPage={currentPage}>
          <FormattedMessage id="Topbar.nav.discover" />
        </MobileNavItem>
        <MobileNavItem
          name="CMSPage"
          params={{ pageId: 'team' }}
          pageName="CMSPage"
          route={{ name: 'CMSPage', params: { pageId: 'team' } }}
          currentPage={currentPage}
        >
          <FormattedMessage id="Topbar.nav.team" />
        </MobileNavItem>
        <MobileNavItem
          name="CMSPage"
          params={{ pageId: 'media' }}
          pageName="CMSPage"
          route={{ name: 'CMSPage', params: { pageId: 'media' } }}
          currentPage={currentPage}
        >
          <FormattedMessage id="Topbar.nav.media" />
        </MobileNavItem>
        <MobileNavItem currentPage={currentPage} onClick={onSearchClick}>
          <FormattedMessage id="Topbar.nav.search" />
        </MobileNavItem>
        <MobileNavItem
          name="PrivateAccessPage"
          pageName="PrivateAccessPage"
          currentPage={currentPage}
        >
          <FormattedMessage id="Topbar.nav.privateAccess" />
        </MobileNavItem>
        <li className={css.languageNavigationItem}>
          <LanguageMenu mobile />
        </li>
        {!isServiceProvider ? (
          <li className={classNames(css.navigationItem, currentPageClass(`InboxPage:${inboxTab}`))}>
            <NamedLink name="InboxPage" params={{ tab: inboxTab }} className={css.navigationLink}>
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink>
          </li>
        ) : null}
        {manageListingsLinkMaybe}
        {createListingsLinkMaybe}
        {isAdmin ? (
          <li className={classNames(css.navigationItem, currentPageClass('MeetingsPage'))}>
            <NamedLink name="MeetingsPage" className={css.navigationLink}>
              <FormattedMessage id="TopbarMobileMenu.meetingsLink" />
            </NamedLink>
          </li>
        ) : null}
        {/* {isAdmin ? (
          <li className={classNames(css.navigationItem, currentPageClass('RezdyBookingsPage'))}>
            <NamedLink name="RezdyBookingsPage" className={css.navigationLink}>
              <FormattedMessage id="TopbarMobileMenu.rezdyBookingsLink" />
            </NamedLink>
          </li>
        ) : null} */}
        {isAdmin ? (
          <li className={classNames(css.navigationItem, currentPageClass('InviteProviderPage'))}>
            <NamedLink name="InviteProviderPage" className={css.navigationLink}>
              <FormattedMessage id="TopbarMobileMenu.inviteProviderLink" />
            </NamedLink>
          </li>
        ) : null}
        <li className={classNames(css.navigationItem, currentPageClass('AccountSettingsPage'))}>
          <NamedLink name="AccountSettingsPage" className={css.navigationLink}>
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </li>
        <li className={css.navigationItem}>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </li>
      </ul>

      <div className={css.footer}>
        <NamedLink name="ProfileSettingsPage" className={css.avatarLink}>
          <AvatarMedium
            user={currentUser}
            disableProfileLink
            rootClassName={css.footerAvatar}
            initialsClassName={css.footerAvatarInitials}
          />
        </NamedLink>
      </div>
    </nav>
  );
};

export default TopbarMobileMenu;
